import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, uploadFile, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Send, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ForumTopic() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [replyImage, setReplyImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/forum/topics/${id}`);
    setData(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const attachImage = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setReplyImage(url);
      toast.success("Image attached");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!reply.trim() && !replyImage) return;
    setSending(true);
    try {
      await api.post(`/forum/topics/${id}/posts`, { body: reply, image_url: replyImage });
      setReply("");
      setReplyImage("");
      load();
    } catch (e) {
      toast.error("Could not reply");
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-page animate-pulse font-tech">Loading…</div>;
  const { topic, posts } = data;

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
      <Link to="/forum" className="inline-flex items-center gap-1 text-sm text-muted-page hover:brand-text mb-6"><ArrowLeft className="w-4 h-4" /> Back to forum</Link>

      <div className="surface border rounded-2xl p-6 mb-6">
        <h1 className="font-display text-3xl font-bold">{topic.title}</h1>
        <p className="text-xs text-muted-page mt-1 mb-4">Started by {topic.author_id ? <Link to={`/messages/${topic.author_id}`} className="brand-text">{topic.author_name}</Link> : <span className="text-muted-page italic">{topic.author_name}</span>}</p>
        {topic.body && <p className="leading-relaxed whitespace-pre-wrap">{topic.body}</p>}
        {topic.image_url && <img src={mediaUrl(topic.image_url)} alt="attachment" data-testid="topic-image" className="mt-4 max-h-96 rounded-xl border" style={{ borderColor: "var(--page-border)" }} />}
      </div>

      <h2 className="font-display text-xl font-bold mb-3">{posts.length} {posts.length === 1 ? "Reply" : "Replies"}</h2>
      <div className="space-y-3 mb-6" data-testid="forum-posts">
        {posts.map((p) => (
          <div key={p.id} className="surface border rounded-xl p-4" data-testid={`forum-post-${p.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-full grid place-items-center text-xs font-bold" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>{(p.author_name || "?")[0].toUpperCase()}</span>
              {p.author_id ? <Link to={`/messages/${p.author_id}`} className="font-semibold text-sm hover:brand-text">{p.author_name}</Link> : <span className="font-semibold text-sm text-muted-page italic">{p.author_name}</span>}
            </div>
            <p className="leading-relaxed whitespace-pre-wrap text-sm">{p.body}</p>
            {p.image_url && <img src={mediaUrl(p.image_url)} alt="attachment" data-testid={`post-image-${p.id}`} className="mt-3 max-h-80 rounded-lg border" style={{ borderColor: "var(--page-border)" }} />}
          </div>
        ))}
      </div>

      {user ? (
        <div className="surface border rounded-2xl p-4">
          <textarea data-testid="reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Add your reply…" className="w-full surface border rounded-lg px-3 py-2.5 mb-3" />
          {replyImage && (
            <div className="relative inline-block mb-3">
              <img src={mediaUrl(replyImage)} alt="attachment" className="max-h-40 rounded-lg border" style={{ borderColor: "var(--page-border)" }} />
              <button type="button" data-testid="remove-reply-image-btn" onClick={() => setReplyImage("")} className="absolute -top-2 -right-2 w-6 h-6 rounded-full grid place-items-center text-white" style={{ background: "var(--brand)" }}><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold surface border cursor-pointer text-sm" data-testid="reply-image-input">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Photo
              <input type="file" accept="image/*" className="hidden" onChange={attachImage} disabled={uploading} />
            </label>
            <button data-testid="submit-reply-btn" onClick={submit} disabled={sending || uploading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold disabled:opacity-60" style={{ background: "var(--brand)", color: "#fff" }}><Send className="w-4 h-4" /> Reply</button>
          </div>
        </div>
      ) : <Link to="/login" className="brand-text honey-underline">Sign in to reply</Link>}
    </div>
  );
}
